import { lazy, Suspense, useEffect } from "react";
import { LuGift, LuHouse, LuGamepad2, LuTrophy, LuStore, LuUser, LuUsers, LuChartColumn, LuGraduationCap, LuCircleHelp, LuGlobe, LuUserPlus } from "react-icons/lu";
import { Link, match, navigate, usePath } from "./router";
import { useApp } from "./store";
import { Avatar } from "./components/Avatar";
import { Coins, Spinner } from "./components/ui";
import { InstallButton } from "./components/InstallButton";
import { RunTransition } from "./components/RunTransition";
import { LevelUpWatcher } from "./components/levelup";
import { SoundSwitch } from "./components/juice";
import { installEffectGuard, installPointerFx, installReveal, installScrollFx } from "./components/fx";
import { SLOGAN, SiteFooter } from "./components/Showcase";
import { initSound, installSoundUnlock } from "./sound";
import { Landing, Login, Signup, Welcome } from "./pages/Auth";
import { Dashboard } from "./pages/Dashboard";
import { GameList, GameSetup } from "./pages/Games";
import { MatchPage } from "./pages/Match";
import { Achievements } from "./pages/Achievements";
import { Shop } from "./pages/Shop";
import { Leaderboard } from "./pages/Leaderboard";
import { ClassroomStudent } from "./pages/Classroom";
import { Profile } from "./pages/Profile";
import { Help } from "./pages/Help";
import { TeacherHome, TeacherClass, TeacherSet, TeacherSession } from "./pages/Teacher";
import { SessionStudent } from "./pages/Classroom";
import { Friends } from "./pages/Friends";
import { useFriendSummary } from "./components/friendbadge";

/** Fri Verden (3D) is a big chunk – it is only fetched when somebody walks in */
const World = lazy(() => import("./pages/World"));

function NotFound() {
  return (
    <div className="card center">
      <h1>Siden findes ikke</h1>
      <p className="muted">Måske er linket gammelt, eller også har du tastet forkert.</p>
      <Link to="/" className="btn">
        Til forsiden
      </Link>
    </div>
  );
}

const STUDENT_NAV = [
  { to: "/", label: "Hjem", icon: LuHouse },
  { to: "/spil", label: "Spil", icon: LuGamepad2 },
  { to: "/verden", label: "Verden", icon: LuGlobe },
  { to: "/praemier", label: "Mål", icon: LuTrophy },
  { to: "/butik", label: "Shop", icon: LuStore },
  { to: "/venner", label: "Venner", icon: LuUserPlus },
  { to: "/rangliste", label: "Rangliste", icon: LuChartColumn },
  { to: "/klasse", label: "Klasse", icon: LuUsers },
  { to: "/profil", label: "Profil", icon: LuUser },
];
/** mobile bottom bar: the big round "Spil" button sits in the middle, two destinations on each side */
const STUDENT_TABS = [
  { to: "/", label: "Hjem", icon: LuHouse },
  { to: "/verden", label: "Verden", icon: LuGlobe },
  { to: "/spil", label: "Spil", icon: LuGamepad2, mid: true },
  { to: "/butik", label: "Shop", icon: LuStore },
  { to: "/venner", label: "Venner", icon: LuUserPlus },
];
const TEACHER_NAV = [
  { to: "/laerer", label: "Mine klasser", icon: LuGraduationCap },
  { to: "/spil", label: "Prøv spillene", icon: LuGamepad2 },
  { to: "/profil", label: "Profil", icon: LuUser },
  { to: "/hjaelp", label: "Hjælp", icon: LuCircleHelp },
];

function Shell({ children }: { children?: any }) {
  const { me, online } = useApp();
  const path = usePath();
  const nav = me!.role === "student" ? STUDENT_NAV : TEACHER_NAV;
  const isCur = (to: string) => (to === "/" ? path === "/" : path === to || path.startsWith(to + "/"));
  const tab: { to: string; label: string; icon: any; mid?: boolean }[] = me!.role === "student" ? STUDENT_TABS : TEACHER_NAV;
  const lp = me!.levelProgress;
  const student = me!.role === "student";
  /* v22: teachers start without sound (a classroom), children with it – the speaker in the top bar remembers the choice */
  useEffect(() => { initSound(!student); }, [student]);
  const fr = useFriendSummary(student);
  /** "Venner": new requests + invitations · "Mål": finished goals waiting to be collected ("Indløs") */
  const badge = (to: string) => (to === "/venner" ? fr.incoming + fr.invites : to === "/praemier" && student ? me!.claimable ?? 0 : 0);
  const badgeEl = (to: string) => {
    const n = badge(to);
    if (n <= 0) return null;
    return to === "/praemier"
      ? <b className="navbadge gift" role="img" aria-label={n === 1 ? "1 præmie klar til at hente" : `${n} præmier klar til at hente`}><LuGift aria-hidden="true" size={12} />{n}</b>
      : <b className="navbadge" aria-label={`${n} nye`}>{n}</b>;
  };
  return (
    <div className="app">
      <a href="#main" className="skip">
        Spring til indhold
      </a>
      <header className="topnav">
        <Link to={student ? "/" : "/laerer"} className="brand" aria-label="EduBattle – forside">
          <span className="logo" aria-hidden="true"><LuGraduationCap size={22} /></span>
          <span className="bt">EduBattle</span>
        </Link>
        <nav className="nav" aria-label="Navigation">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} className={n.to === "/spil" ? "play" : undefined} aria-current={isCur(n.to) ? "page" : undefined}>
              <n.icon aria-hidden="true" size={18} />
              {n.label}
              {badgeEl(n.to)}
            </Link>
          ))}
          {student && (
            <Link to="/hjaelp" aria-current={isCur("/hjaelp") ? "page" : undefined}>
              <LuCircleHelp aria-hidden="true" size={18} />
              <span className="sr-only">Hjælp</span>
            </Link>
          )}
        </nav>
        <div className="topright">
          {student && (
            <div className="xpbox" title={`${lp.into} / ${lp.needed} XP til næste niveau`}>
              <span className="hex" aria-label={`Niveau ${me!.level}`}>Lv.{me!.level}</span>
              <div style={{ flex: 1 }}>
                <div className="xpbar" role="progressbar" aria-label="XP til næste niveau" aria-valuemin={0} aria-valuemax={lp.needed} aria-valuenow={lp.into}>
                  <i style={{ width: `${Math.min(100, Math.round((lp.into / Math.max(1, lp.needed)) * 100))}%` }} />
                </div>
              </div>
            </div>
          )}
          {student && <Coins n={me!.balance} />}
          <SoundSwitch />
          <InstallButton className="in-nav" />
          <Link to="/profil" className="userbtn" aria-label={`Profil: ${me!.username}`}>
            <Avatar equipped={me!.avatar} size={38} label="" />
            <strong>{me!.username}</strong>
          </Link>
        </div>
      </header>
      {!online && <div className="offline-bar" role="status">Du er offline. Spil kræver internet – dine mønter og fremskridt gemmes på serveren.</div>}
      <main id="main" className="main" tabIndex={-1}>
        {children}
        <SiteFooter />
      </main>
      {student && (me!.claimable ?? 0) > 0 && path !== "/" && path !== "/praemier" && !path.startsWith("/verden") && !path.startsWith("/spil/") && (
        <Link to="/praemier" className="claim-pill" aria-label={`${me!.claimable} præmier er klar til at hente under Mål`}>
          <LuGift aria-hidden="true" size={18} /> {me!.claimable === 1 ? "1 præmie er klar" : `${me!.claimable} præmier er klar`} <b>Hent</b>
        </Link>
      )}
      <nav className="tabbar" aria-label="Hurtig navigation">
        {tab.map((n) => (
          <Link key={n.to} to={n.to} className={n.mid ? "mid" : undefined} aria-current={isCur(n.to) ? "page" : undefined}>
            {n.mid ? <span className="bubble"><n.icon aria-hidden="true" size={30} /></span> : <n.icon aria-hidden="true" size={22} />}
            <span>{n.label}</span>
            {badgeEl(n.to)}
          </Link>
        ))}
      </nav>
      <RunTransition equipped={me!.avatar} />
      {student && <LevelUpWatcher />}
    </div>
  );
}

export function App() {
  const { me, loading, online } = useApp();
  const path = usePath();

  useEffect(() => {
    document.title = `EduBattle – ${SLOGAN}`;
  }, []);
  /* tilt, glare, spotlight and parallax that follow the mouse (mouse only; skipped with reduced motion) */
  useEffect(() => installPointerFx(), []);
  useEffect(() => installReveal(), []);
  useEffect(() => installScrollFx(), []);
  /* slow computer? then the pure decoration (floating things, sparkles, drifting clouds) switches itself off */
  useEffect(() => installEffectGuard(), []);
  useEffect(() => installSoundUnlock(), []);

  if (loading) return <div style={{ padding: 40 }}><Spinner label="Starter EduBattle…" /></div>;

  const pub = ["/login", "/opret", "/hjaelp"];
  if (!me) {
    if (path === "/login") return <Login />;
    if (path === "/opret") return <Signup />;
    if (path === "/hjaelp") return <div className="main"><Help /></div>;
    if (path !== "/") navigate("/", true);
    return <Landing offline={!online} />;
  }
  void pub;
  if (me.role === "student" && me.onboardingStep < 3 && path !== "/velkommen") {
    navigate("/velkommen", true);
    return null;
  }
  if (path === "/velkommen") return <Welcome />;
  if (path === "/login" || path === "/opret") {
    navigate("/", true);
    return null;
  }

  let page: any = null;
  let m: ReturnType<typeof match>;
  const teacher = me.role !== "student";
  if (path === "/") {
    if (teacher) {
      navigate("/laerer", true);
      return null;
    }
    page = <Dashboard />;
  } else if (path === "/spil") page = <GameList />;
  else if (path === "/spil/verden" && !teacher) return <Suspense fallback={<div style={{ padding: 40 }}><Spinner label="Åbner Fri Verden…" /></div>}><World /></Suspense>; // Fri Verden has its own start screen
  else if ((m = match("/spil/:game", path))) page = <GameSetup game={m.params.game!} />;
  else if ((m = match("/kamp/:id", path))) page = <MatchPage id={m.params.id!} />;
  else if (path === "/praemier") page = <Achievements />;
  else if (path === "/butik") page = <Shop />;
  else if (path === "/rangliste") page = <Leaderboard />;
  else if (path === "/klasse") page = <ClassroomStudent />;
  else if (path === "/venner" && !teacher) page = <Friends />;
  else if (path === "/verden" && !teacher) return <Suspense fallback={<div style={{ padding: 40 }}><Spinner label="Åbner Fri Verden…" /></div>}><World /></Suspense>;
  else if ((m = match("/seance/:id", path))) page = <SessionStudent id={Number(m.params.id)} />;
  else if (path === "/profil") page = <Profile />;
  else if (path === "/hjaelp") page = <Help />;
  else if (teacher && path === "/laerer") page = <TeacherHome />;
  else if (teacher && (m = match("/laerer/klasse/:id", path))) page = <TeacherClass id={Number(m.params.id)} />;
  else if (teacher && (m = match("/laerer/saet/:id", path))) page = <TeacherSet id={Number(m.params.id)} />;
  else if (teacher && (m = match("/laerer/seance/:id", path))) page = <TeacherSession id={Number(m.params.id)} />;
  else page = <NotFound />;

  return <Shell>{page}</Shell>;
}
